import React, { useMemo } from "react";

/* Ambient atmosphere for the HOF page:
   - 3 slow sweeping spotlights (colored cones)
   - A field of fading dust/dot particles
   - A soft vignette
   All CSS-only, no deps, pointer-events disabled. */
const DOT_COLORS = ["#FFD700", "#FF007F", "#00F0FF", "#39FF14", "#9D00FF"];

export default function HOFAmbient() {
  const dots = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        bottom: 20 + Math.random() * 60,
        size: 2 + Math.random() * 3,
        delay: Math.random() * 6,
        duration: 5 + Math.random() * 6,
        drift: (Math.random() - 0.5) * 60,
        color: DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)],
      })),
    []
  );

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden z-[2]"
      data-testid="hof-ambient"
      aria-hidden="true"
    >
      {/* Spotlights */}
      <div className="hof-spot hof-spot-a" />
      <div className="hof-spot hof-spot-b" />
      <div className="hof-spot hof-spot-c" />

      {/* Dust dots */}
      {dots.map((d) => (
        <span
          key={d.id}
          className="absolute hof-dot"
          style={{
            left: `${d.left}%`,
            bottom: `${d.bottom}%`,
            width: `${d.size}px`,
            height: `${d.size}px`,
            background: d.color,
            boxShadow: `0 0 ${d.size * 2}px ${d.color}`,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
            "--drift": `${d.drift}px`,
          }}
        />
      ))}

      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, #0A051AAA 95%)",
        }}
      />

      <style>{`
        .hof-spot {
          position: absolute;
          top: -30%;
          width: 48%;
          height: 150%;
          pointer-events: none;
          mix-blend-mode: screen;
          opacity: 0.55;
          filter: blur(24px);
          transform-origin: 50% 0%;
          will-change: transform, opacity;
        }
        .hof-spot-a {
          left: 10%;
          background: radial-gradient(ellipse at top,
            #FFD70088 0%, #FFD70044 25%, transparent 60%);
          animation: hof-spot-sway-a 11s ease-in-out infinite;
        }
        .hof-spot-b {
          left: 42%;
          background: radial-gradient(ellipse at top,
            #FF007F77 0%, #FF007F33 25%, transparent 60%);
          animation: hof-spot-sway-b 13s ease-in-out infinite;
        }
        .hof-spot-c {
          left: 74%;
          background: radial-gradient(ellipse at top,
            #00F0FF77 0%, #00F0FF33 25%, transparent 60%);
          animation: hof-spot-sway-c 9s ease-in-out infinite;
        }
        @keyframes hof-spot-sway-a {
          0%,100% { transform: rotate(-18deg) translateX(0); opacity: 0.5; }
          50%     { transform: rotate( 14deg) translateX(4%); opacity: 0.75; }
        }
        @keyframes hof-spot-sway-b {
          0%,100% { transform: rotate( 10deg) translateX(0); opacity: 0.4; }
          50%     { transform: rotate(-14deg) translateX(-3%); opacity: 0.7; }
        }
        @keyframes hof-spot-sway-c {
          0%,100% { transform: rotate(-10deg) translateX(0); opacity: 0.45; }
          50%     { transform: rotate( 18deg) translateX(-5%); opacity: 0.75; }
        }

        .hof-dot {
          position: absolute;
          border-radius: 9999px;
          opacity: 0;
          animation-name: hof-dot-float;
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
          will-change: transform, opacity;
        }
        @keyframes hof-dot-float {
          0%   { opacity: 0; transform: translate3d(0, 20px, 0) scale(0.6); }
          20%  { opacity: 1; }
          50%  { transform: translate3d(calc(var(--drift) / 2), -30px, 0) scale(1); }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translate3d(var(--drift), -80px, 0) scale(0.4); }
        }
      `}</style>
    </div>
  );
}
